import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { gql, useQuery, useMutation, useApolloClient } from '@apollo/client';
import { useUser } from '@clerk/clerk-react';
import { ME_QUERY } from '../../graphql/queries';

const MY_ONBOARDING_SESSION = gql`
  query MyOnboardingSession {
    myOnboardingSession {
      id
      currentStepCode
      currentStepPayload
      completedRequiredCount
      completedTotalCount
      carriedStepCodes
      outcome
      startedAt
      completedAt
      lastActiveAt
      flowVersion {
        id
        version
        name
        isDefault
        steps {
          id
          stepCode
          position
          isEnabled
          configOverrides
          config
          isRequired
          description
          rationale
        }
      }
    }
  }
`;

const ASSIGN_ONBOARDING_FLOW = gql`
  mutation AssignOnboardingFlow {
    assignOnboardingFlow {
      id
      currentStepCode
      completedRequiredCount
      completedTotalCount
      outcome
    }
  }
`;

const RECORD_ONBOARDING_STEP_EVENT = gql`
  mutation RecordOnboardingStepEvent($input: RecordOnboardingStepEventInput!) {
    recordOnboardingStepEvent(input: $input) {
      id
      currentStepCode
      completedRequiredCount
      completedTotalCount
      outcome
    }
  }
`;

/**
 * Quiet variant: selects only `id` so the response can't teach the Apollo cache a new
 * currentStepCode. The critique post records and emails without advancing; the open
 * confirmation stays until Next, which is a normal submit and does refetch.
 */
const RECORD_ONBOARDING_STEP_EVENT_QUIET = gql`
  mutation RecordOnboardingStepEventQuiet($input: RecordOnboardingStepEventInput!) {
    recordOnboardingStepEvent(input: $input) {
      id
    }
  }
`;
import { welcomeCompletedKey } from '../../constants/onboarding';
import { isCompletionStep } from './step-registry';
import { useAnalytics } from '../../context/analytics';
import { useWaiting } from './useWaiting';
import {
  isOnboardingCompletedRecently,
  setDeferredPendingIntent,
  tryFireInitiateCheckout,
} from '../../utils/initiateCheckoutPixel';

interface OnboardingFlowVersionStep {
  id: string;
  stepCode: string;
  position: number;
  isEnabled: boolean;
  configOverrides?: Record<string, unknown>;
  config: Record<string, unknown>;
  isRequired: boolean;
  description?: string;
  rationale?: string;
}

interface OnboardingFlowVersion {
  id: string;
  version: number;
  name: string;
  isDefault: boolean;
  steps: OnboardingFlowVersionStep[];
}

interface OnboardingSession {
  id: string;
  currentStepCode: string;
  currentStepPayload?: Record<string, unknown> | null;
  completedRequiredCount: number;
  completedTotalCount: number;
  /** Steps answered in the waiting wizard before they were let in; this flow skips them. */
  carriedStepCodes?: string[];
  outcome: 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
  startedAt: string;
  completedAt?: string;
  lastActiveAt: string;
  flowVersion: OnboardingFlowVersion;
}

interface WizardData {
  [key: string]: unknown;
}

/**
 * What the server made of a submit. `outcome` is the session's, not the step's: a submit on
 * the last step only completes the session when the required-step quota is met, and otherwise
 * rewinds `currentStepCode` to the first incomplete required step. Null when the mutation
 * failed and there is nothing to read.
 */
interface StepSubmitResult {
  outcome: 'IN_PROGRESS' | 'COMPLETED' | 'ABANDONED';
  currentStepCode: string;
}

interface SubmitStepOptions {
  /**
   * Record the submit but keep the current render: no session refetch. The critique
   * post uses this so the confirmation stays put. That submit also leaves
   * currentStepCode on COMPLETION_CRITIQUE; Next is a later empty submit, which
   * advances and refetches.
   */
  skipRefetch?: boolean;
}

interface WelcomeContextValue {
  session: OnboardingSession | null;
  loading: boolean;
  data: WizardData;
  updateData: (patch: Partial<WizardData>) => void;
  submitStep: (
    stepCode: string,
    payload: Record<string, unknown>,
    durationMs?: number,
    options?: SubmitStepOptions
  ) => Promise<StepSubmitResult | null>;
  /** Re-pull the session + user (the same refetch submitStep does). */
  refreshSession: () => Promise<void>;
  skipStep: (stepCode: string) => Promise<void>;
  goBack: () => Promise<void>;
  currentStep: OnboardingFlowVersionStep | null;
  steps: OnboardingFlowVersionStep[];
  stepIndex: number;
  /** True while a step submit/skip/back is in flight; drives the loading bar + nav disable. */
  isAdvancing: boolean;
  arrivedViaBack: boolean;
  /** True when rendered inside the admin preview modal — disables real mutations. */
  isPreview?: boolean;
}

export const WelcomeContext = createContext<WelcomeContextValue | undefined>(undefined);
export type {
  WelcomeContextValue,
  OnboardingFlowVersionStep,
  OnboardingFlowVersion,
  OnboardingSession,
  StepSubmitResult,
};

export function WelcomeProvider({ children }: { children: React.ReactNode }) {
  const [data, setData] = useState<WizardData>({});
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [arrivedViaBack, setArrivedViaBack] = useState(false);
  const [assignFlow] = useMutation(ASSIGN_ONBOARDING_FLOW);
  const [recordEvent] = useMutation(RECORD_ONBOARDING_STEP_EVENT);
  const [recordEventQuiet] = useMutation(RECORD_ONBOARDING_STEP_EVENT_QUIET);
  const assignedRef = useRef(false);
  const hasFiredCompletion = useRef(false);
  const prevOutcomeRef = useRef<string | null | undefined>(undefined);
  const pixelPendingRef = useRef(false);
  const lastViewedStepRef = useRef<string | null>(null);
  const { amplitude, fbPixel, gtm } = useAnalytics();
  const apolloClient = useApolloClient();
  const { user: clerkUser } = useUser();
  const asfId = clerkUser?.publicMetadata?.asfId as string | undefined;
  // A held member finishing the waiting wizard has not finished onboarding: InitiateCheckout waits
  // for the new-member steps after they're let in (the waiting finish is Meta `Schedule`).
  const { waiting, loading: accessLoading } = useWaiting();

  const { data: sessionData, loading } = useQuery(MY_ONBOARDING_SESSION, {
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    if (!assignedRef.current) {
      assignedRef.current = true;
      assignFlow({ refetchQueries: [{ query: MY_ONBOARDING_SESSION }] }).catch((err) => {
        console.error('Failed to assign onboarding flow:', err);
      });
    }
  }, [assignFlow]);

  const session = sessionData?.myOnboardingSession ?? null;

  const steps = useMemo(() => {
    return (session?.flowVersion?.steps ?? [])
      .filter((s: OnboardingFlowVersionStep) => s.isEnabled)
      .sort((a: OnboardingFlowVersionStep, b: OnboardingFlowVersionStep) => a.position - b.position);
  }, [session]);

  const currentStep = useMemo(() => {
    return steps.find((s: OnboardingFlowVersionStep) => s.stepCode === session?.currentStepCode) ?? steps[0] ?? null;
  }, [steps, session?.currentStepCode]);

  const stepIndex = useMemo(() => {
    return Math.max(
      0,
      steps.findIndex((s: OnboardingFlowVersionStep) => s.stepCode === currentStep?.stepCode)
    );
  }, [steps, currentStep]);

  const updateData = useCallback((patch: Partial<WizardData>) => {
    setData((prev) => ({ ...prev, ...patch }));
  }, []);

  const flowVersionProps = useMemo(
    () => ({
      flow_version_id: session?.flowVersion.id ?? '',
      flow_version_number: session?.flowVersion.version ?? 0,
      flow_version_name: session?.flowVersion.name ?? '',
    }),
    [session?.flowVersion.id, session?.flowVersion.version, session?.flowVersion.name]
  );

  const refreshSession = useCallback(async () => {
    await Promise.allSettled([
      apolloClient.query({ query: MY_ONBOARDING_SESSION, fetchPolicy: 'network-only' }),
      apolloClient.query({ query: ME_QUERY, fetchPolicy: 'network-only' }),
    ]);
  }, [apolloClient]);

  const submitStep = useCallback(
    async (stepCode: string, payload: Record<string, unknown>, durationMs?: number, options?: SubmitStepOptions) => {
      setIsAdvancing(true);
      setArrivedViaBack(false);
      try {
        // Deliberately NOT `refetchQueries` + `awaitRefetchQueries`: those make a failed
        // refetch reject the whole mutation, so a submit that landed and left the session
        // IN_PROGRESS would come back as an unknown outcome — and the finale reads an
        // unknown outcome as "she is done" and leaves. The write's answer and the reads
        // that follow it are two different failures, so they are awaited separately.
        const mutate = options?.skipRefetch ? recordEventQuiet : recordEvent;
        const { data: result } = await mutate({
          variables: {
            input: {
              stepCode,
              eventType: 'SUBMITTED',
              payload,
              durationMs,
            },
          },
        });
        // Still awaited: the caller may be about to act on the refreshed session, and a
        // refetch that fails leaves the wizard on stale data rather than losing the
        // outcome above. skipRefetch keeps the current render (quiet submit).
        if (!options?.skipRefetch) {
          await refreshSession();
        }
        amplitude.track('onboarding_step_submitted', {
          step_code: stepCode,
          position: stepIndex + 1,
          ...flowVersionProps,
          duration_ms: durationMs ?? 0,
          used_fallback: (payload?.fallback as boolean | undefined) ?? false,
        });
        // The mutation's own session is the only reading of "did this finish?" a caller can
        // trust in the moment: `session` here is the render's, one refetch behind the click.
        return (result?.recordOnboardingStepEvent as StepSubmitResult | undefined) ?? null;
      } finally {
        setIsAdvancing(false);
      }
    },
    [recordEvent, recordEventQuiet, refreshSession, stepIndex, amplitude, flowVersionProps]
  );

  const skipStep = useCallback(
    async (stepCode: string, durationMs?: number) => {
      setIsAdvancing(true);
      setArrivedViaBack(false);
      try {
        await recordEvent({
          variables: {
            input: {
              stepCode,
              eventType: 'SKIPPED',
              durationMs,
            },
          },
          refetchQueries: [{ query: MY_ONBOARDING_SESSION }],
          awaitRefetchQueries: true,
        });
        amplitude.track('onboarding_step_skipped', {
          step_code: stepCode,
          position: stepIndex + 1,
          ...flowVersionProps,
          duration_ms: durationMs ?? 0,
        });
      } finally {
        setIsAdvancing(false);
      }
    },
    [recordEvent, stepIndex, amplitude, flowVersionProps]
  );

  const goBack = useCallback(async () => {
    if (!currentStep) return;
    const fromStepCode = currentStep.stepCode;
    setIsAdvancing(true);
    setArrivedViaBack(true);
    try {
      await recordEvent({
        variables: {
          input: {
            stepCode: fromStepCode,
            eventType: 'BACK',
          },
        },
        refetchQueries: [{ query: MY_ONBOARDING_SESSION }],
        awaitRefetchQueries: true,
      });
      const previousStep = steps[Math.max(0, stepIndex - 1)];
      if (previousStep) {
        amplitude.track('onboarding_step_back', {
          from_step_code: fromStepCode,
          to_step_code: previousStep.stepCode,
          ...flowVersionProps,
        });
      }
    } catch (error) {
      setArrivedViaBack(false);
      throw error;
    } finally {
      setIsAdvancing(false);
    }
  }, [recordEvent, currentStep, steps, stepIndex, amplitude, flowVersionProps]);

  useEffect(() => {
    if (!session) return;
    const stepCode = session.currentStepCode;
    // Completion steps render after the outcome flips, so they still record VIEWED —
    // the funnel would otherwise be blind to the whole completion zone. A plain
    // completed session (currentStepCode on a normal step) records nothing.
    const viewable =
      session.outcome === 'IN_PROGRESS' || (session.outcome === 'COMPLETED' && isCompletionStep(stepCode));
    if (!viewable) return;
    if (!stepCode) return;
    if (lastViewedStepRef.current === stepCode) return;
    lastViewedStepRef.current = stepCode;

    const flowVersion = session.flowVersion;
    const position = stepIndex + 1;
    recordEvent({
      variables: {
        input: {
          stepCode,
          eventType: 'VIEWED',
        },
      },
    })
      .then(() => {
        amplitude.track('onboarding_step_viewed', {
          step_code: stepCode,
          position,
          flow_version_id: flowVersion.id,
          flow_version_number: flowVersion.version,
          flow_version_name: flowVersion.name,
        });
      })
      .catch((err) => {
        console.error('Failed to record onboarding viewed event:', err);
      });
  }, [session, stepIndex, recordEvent, amplitude]);

  useEffect(() => {
    if (!session) return;

    const prevOutcome = prevOutcomeRef.current;
    const isInitialSessionLoad = prevOutcome === undefined;
    const justCompleted = session.outcome === 'COMPLETED' && !isInitialSessionLoad && prevOutcome !== 'COMPLETED';
    const completedRecently =
      isInitialSessionLoad && session.outcome === 'COMPLETED' && isOnboardingCompletedRecently(session.completedAt);
    prevOutcomeRef.current = session.outcome;

    if (session.outcome !== 'COMPLETED') return;

    if (!hasFiredCompletion.current) {
      hasFiredCompletion.current = true;

      const totalDuration = new Date().getTime() - new Date(session.startedAt).getTime();
      amplitude.track('onboarding_completed', {
        flow_version_id: session.flowVersion.id,
        flow_version_number: session.flowVersion.version,
        flow_version_name: session.flowVersion.name,
        total_duration_ms: totalDuration,
        completed_required_count: session.completedRequiredCount,
        completed_total_count: session.completedTotalCount,
      });

      if (justCompleted || completedRecently) {
        pixelPendingRef.current = true;
        setDeferredPendingIntent(session.id);
      }
    }

    // Clerk metadata may lag — retry client completion hints when asfId arrives.
    if (!asfId) return;
    // A held member finished the waiting wizard, not onboarding: no "completed" hints (they would
    // let the app's routes in) and no InitiateCheckout until the new-member steps after they're in.
    if (accessLoading || waiting) return;

    const completedAt = session.completedAt ?? new Date().toISOString();

    try {
      apolloClient.cache.modify({
        id: apolloClient.cache.identify({ __typename: 'User', asfId }),
        fields: {
          welcomeCompletedAt: () => completedAt,
        },
      });
    } catch {
      /* ignore */
    }

    try {
      sessionStorage.setItem(welcomeCompletedKey(asfId), completedAt);
    } catch {
      /* private mode / quota — fall back to cache patch above */
    }

    const pixelPending = pixelPendingRef.current;
    const fired = tryFireInitiateCheckout(asfId, session.id, fbPixel, gtm, {
      justCompleted,
      pixelPendingRef: pixelPending,
      completedAt: session.completedAt,
      completedRecently,
    });
    if (fired) {
      pixelPendingRef.current = false;
    }
  }, [session?.outcome, session, amplitude, fbPixel, gtm, apolloClient, asfId, accessLoading, waiting]);

  const value = {
    session,
    loading,
    data,
    updateData,
    submitStep,
    refreshSession,
    skipStep,
    goBack,
    currentStep,
    steps,
    stepIndex,
    isAdvancing,
    arrivedViaBack,
    isPreview: false,
  };

  return <WelcomeContext.Provider value={value}>{children}</WelcomeContext.Provider>;
}

export function useWelcomeContext() {
  const context = useContext(WelcomeContext);
  if (context === undefined) {
    throw new Error('useWelcomeContext must be used within a WelcomeProvider');
  }
  return context;
}
